import React from 'react';
import { useIntl } from 'react-intl';
import { useSearchParams } from 'react-router-dom';

import { useApplicationRuntimeContext } from '@context/ApplicationRuntimeContext';
import { useFormatUnit } from '@hooks';
import { IProductAttributes, IProductAttributesResponse, EProductAttribute } from '@model';
import { featuresMessages } from '@services/localizationService';
import { RouteParams } from '@services/routingService';
import { EngravingItemBody, IGeneralProductItem, ProductItemBodyWrapper } from '@sharedComponents/Products';
import { SessionStorageKeys } from '@utils/constants';
import { isNotEmpty } from '@utils/general';
import { CypressTestNamespace } from '@utils/test';

import { ECartPageState, useCartPageContext } from '../../../context';
import { createEcommerceProduct } from '../../../ecommerce';
import { messages as generalMessages } from '../../../messages';
import { IProduct } from '../../../model';
import { isProductDisabled } from '../../../utils';
import { messages } from '../messages';
import { sortCartProducts } from '../utils';

export const useAdaptedProductItem = (): IGeneralProductItem[] => {
  const { formatMessage } = useIntl();
  const formatUnit = useFormatUnit(generalMessages);
  const {
    applicationSettings: {
      shopSettings: { orderLimit },
      secondaryCurrency,
      features: { enableLoyalty },
    },
  } = useApplicationRuntimeContext();

  const {
    settings: { voucherUnderProductEnabled },
  } = useCartPageContext();

  const showVoucherUnderProduct = voucherUnderProductEnabled;

  const [searchParams] = useSearchParams();

  const isDualPriceLayout = Boolean(secondaryCurrency);
  const airDeliveryForbidden = (attributes?: IProductAttributesResponse[] | null): boolean => {
    if (!attributes) {
      return false;
    }

    const enumAttributes = attributes as IProductAttributes[];

    return enumAttributes.some(({ key, value }) => {
      const isProhibited = key === EProductAttribute.PROHIBITED && value;
      const isPartlyProhibited =
        key === EProductAttribute.PARTLY_PROHIBITED &&
        value &&
        sessionStorage.getItem(SessionStorageKeys.UNAVAILABLE_AIRPLANE_DELIVERY);

      return isProhibited || isPartlyProhibited;
    });
  };

  const isUrlWithAirDelivery = (): boolean | undefined => {
    const airplaneDelivery = searchParams.get(RouteParams.AIRPLANE_DELIVERY);
    return airplaneDelivery !== null;
  };

  const {
    state: {
      products,
      pricesSummary: { priceProductsSummary, discountWithoutDelivery },
      benefits,
    },
    actions: {
      removeAllItemEntries,
      removeNonStandardProduct,
      updateItem,
      setCartPageState,
      setProductVoucherCode,
      addProductToCart,
    },
    settings: {
      freeDelivery,
      freeDeliveryGlobalFrom,
      freeDeliveryGlobalTo,
      minimalPriceLimit,
      freeDeliveryGlobalLimit,
    },
  } = useCartPageContext();

  return sortCartProducts(products)
    .filter(filterDisplayProducts)
    .map((product: IProduct) => {
      const buttonTextContextHelp = formatMessage(generalMessages.removeProductTitle);
      const buttonText = formatMessage(generalMessages.removeProductButton);
      const totalPriceTextFreeProduct = formatMessage(messages.freeProduct);
      const isTryItFirst = product.tryItFirstId > 0;
      const isNonStandardProduct = isTryItFirst || product.freeProduct || Boolean(product.addedByCoupon);
      const hasCounterDisabled = isNonStandardProduct || product.upselling;

      const handleQuantityChange = async (quantity: number): Promise<boolean> => {
        //if order limit does not exist or user is lowering amount of goods in cart
        const isLimitExceeded =
          orderLimit && priceProductsSummary + product.priceWithVat * (quantity - product.count) > orderLimit;

        if (!isLimitExceeded || product.count >= quantity) {
          return updateItem({
            product,
            newQuantity: quantity,
            minimalPriceLimit,
            freeDelivery,
            freeDeliveryGlobalLimit,
            freeDeliveryGlobalFrom,
            freeDeliveryGlobalTo,
            benefits,
            priceProductsSummary,
            discountWithoutDelivery,
          });
        }

        setCartPageState(ECartPageState.ORDER_LIMIT_NOTIFICATION);

        return false;
      };

      const additionalVariantInfo = product?.additionalVariantInfo ? product.additionalVariantInfo : '';
      const productDisplay: IGeneralProductItem = {
        key: `${product.itemId}-product`,
        isDualPriceLayout: isDualPriceLayout,
        dataTestId: CypressTestNamespace.ECartPageProducts.PRODUCT_WRAPPER,
        price: product.addedByCoupon ? 0 : product.priceWithVat,
        itemQuantity: product.count,
        disabled: isProductDisabled(product),
        maximumQuantity: product.maxOrderQty,
        imageUrl: product.imageUrl,
        itemUrl: product.url,
        priceHistory: product.priceHistory,
        showUnit: false,
        textContent: (
          <ProductItemBodyWrapper
            brand={product.brandName}
            name={product.name}
            subName={product.variant + ' ' + additionalVariantInfo}
            link={product.url}
            disabled={!product.canBuy && !(isTryItFirst || product.autoInserted)}
            isTryItFirst={isTryItFirst}
            usedProduct={product.usedProductInfo}
            isGift={product.freeProduct}
            addedByCoupon={product.addedByCoupon}
            maximumQuantity={product.maxOrderQty}
            itemQuantity={product.count}
            messages={{ ...messages, ...featuresMessages }}
            priceHistory={product.priceHistory}
            action={product.action}
            discount={product.discount}
            airDeliveryForbidden={isUrlWithAirDelivery() && airDeliveryForbidden(product.attributes)}
            showVoucherUnderProduct={showVoucherUnderProduct}
            voucherDiscount={product.voucherDiscount}
            customerVouchers={benefits.vouchers}
            setProductVoucherCode={setProductVoucherCode}
            isLoyalty={product.isLoyalty}
            isMedicinal={product.isMedicinal}
            enableLoyalty={enableLoyalty}
            upselling={product.upselling}
            quantity={product.count}
          />
        ),
        quantityCounterDisabled: hasCounterDisabled,
        unit: formatMessage(generalMessages.pieceUnit),
        ...(product.unitPrice && {
          unitPrice: {
            price: product.unitPrice.price,
            unitAmount: product.unitPrice.amount,
            unit: formatUnit(product.unitPrice.unit),
          },
        }),
        onQuantityChange: handleQuantityChange,
        deleteButtonClick: isNonStandardProduct
          ? () => removeNonStandardProduct({ product })
          : () => removeAllItemEntries({ product }),
        addProductToCart: () =>
          addProductToCart({
            productId: product.productId,
            count: 1,
            product: createEcommerceProduct(product),
            freeDelivery: freeDelivery,
            freeDeliveryGlobalFrom: freeDeliveryGlobalFrom,
            freeDeliveryGlobalTo: freeDeliveryGlobalTo,
            minimalPriceLimit: minimalPriceLimit,
            freeDeliveryGlobalLimit: freeDeliveryGlobalLimit,
            discountWithoutDelivery: discountWithoutDelivery,
            priceProductsSummary: priceProductsSummary,
            benefits: benefits,
          }),
        productsInCartCount: products.length,
        buttonTextContextHelp,
        buttonText,
        totalPriceTextFreeProduct,
        inSaleInfoMarkLink: formatMessage(messages.inSaleInfoMarkLink),
        inSaleInfoMarkTextLink: messages.inSaleInfoMarkTextLink,
        inSaleInfoMarkText: messages.inSaleInfoMarkText,
        productCode: product.productCode,
      };

      const productEngraving: IGeneralProductItem | null = product.engraving
        ? {
            key: `${product.itemId}-engraving`,
            isDualPriceLayout: isDualPriceLayout,
            dataTestId: CypressTestNamespace.ECartPageProducts.ENGRAVING_WRAPPER,
            price: product.engraving.priceWithVat,
            itemQuantity: 1,
            disabled: false,
            maximumQuantity: 1,
            isEngraving: true,
            productsInCartCount: products.length,
            textContent: (
              <EngravingItemBody
                engravingText={product.engraving.value}
                engravingLabel={messages.engravingLabel}
                engravingDeliveryTimeDelay={messages.engravingDeliveryTimeDelay}
              />
            ),
            quantityCounterDisabled: true,
            unit: formatMessage(generalMessages.pieceUnit),
            deleteButtonClick: () => removeNonStandardProduct({ product }),
            buttonTextContextHelp,
            buttonText,
            totalPriceTextFreeProduct,
          }
        : null;

      return [productDisplay, productEngraving].filter(isNotEmpty);
    })
    .reduce((productsList, DisplayAndEngraving) => [...productsList, ...DisplayAndEngraving], []);
};

const filterDisplayProducts = (product: IProduct) =>
  !product.isHidden &&
  (!product.freeProduct ||
    (product.freeProduct && product.autoInserted) ||
    (product.freeProduct && product.tryItFirstId > 0));
